import { useState } from 'react';
import { Button, ConfirmDialog } from '@/shared/components';
import { invoiceKindLabels, type InvoiceResponse } from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';

interface Props {
  invoice: InvoiceResponse;
  discarding: boolean;
  onOpenDraft: (draftId: string) => void;
  onDiscardDraft: (draftId: string) => void;
}

/**
 * Ein Entwurf ist kein Beleg: keine Datei, keine Nummer – er lässt sich wegwerfen. Weil das
 * Löschen endgültig ist und die Eingaben aus dem Builder mitnimmt, fragt vorher ein Dialog.
 */
export function InvoiceDraftItem({ invoice, discarding, onOpenDraft, onDiscardDraft }: Props) {
  const [confirming, setConfirming] = useState(false);
  const kind = invoiceKindLabels[invoice.kind];
  return (
    <div>
      <p className="text-ink-soft mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
        {kind}
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <span className="rounded-pill bg-amber-tint text-amber-ink px-2.5 py-1 text-xs font-bold">
            Entwurf
          </span>
          <span className="text-ink-soft ml-2 text-xs">
            {formatMoney(invoice.totalAmount, invoice.currency)} · noch nicht ausgestellt
          </span>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="min-h-11 sm:min-h-8"
            aria-label={`${kind}-Entwurf weiterbearbeiten`}
            onClick={() => onOpenDraft(invoice.id)}
          >
            Weiterbearbeiten
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-sealwax hover:bg-sealwax/10 min-h-11 sm:min-h-8"
            aria-label={`${kind}-Entwurf verwerfen`}
            onClick={() => setConfirming(true)}
            loading={discarding}
          >
            Verwerfen
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Entwurf verwerfen?"
        description={`Der Entwurf (${kind}, ${formatMoney(invoice.totalAmount, invoice.currency)}) wird gelöscht – mit allen Positionen und Texten. Ausgestellt war er noch nicht.`}
        confirmLabel="Verwerfen"
        cancelLabel="Behalten"
        onConfirm={() => {
          setConfirming(false);
          onDiscardDraft(invoice.id);
        }}
      />
    </div>
  );
}
